import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { formatDate } from '@/utils/format';
import type { FacilityAgreement, Applicant, Consumer, CancelRequest } from "../types";
import { DateFieldBox } from "@/components/DateField";
import InfoDisplay from "@/components/InfoDisplay";
import SidebarNavigation from "@/components/SidebarNavigation";
import Layout from "../../shared/components/Layout";
import AlertMessages from "@/components/AlertMessages";
import type { CancelRequestParams } from "../types";
import getCancel from "../api/getCancel";
import type { Alert } from "@/types";
import clearLocalStorage from "@/utils/clearLocalStorage";
import confirmCancel from "../api/confirmCancel";
import NotFound from "@/template/NotFound";
import { getFormattedToday } from "@/utils/format";

const Cancel: React.FC = () => {
  const { custCode } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [alert, setAlert] = useState<Alert>({
    open: false,
    messages: [],
  })

  const [facilityAgreement, setFacilityAgreement] = useState<FacilityAgreement>();
  const [applicant, setApplicant] = useState<Applicant>();
  const [consumer, setConsumer] = useState<Consumer>();
  const[cancelRequest, setCancelRequest] = useState<CancelRequest>({
    lensCollectionDate: getFormattedToday(true)
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getCancel(custCode);
        setFacilityAgreement(response.data.facilityAgreement);
        setApplicant(response.data.applicant);
        setConsumer(response.data.consumer);

        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        if (error.response && error.response.status === 404) {
          setIsNotFound(true);
        }
        console.error("Error fetching facility lens data:", error);
      }
    };

    fetchData();

    const fromConfirmPage = location.state?.fromConfirmPage;
    if(fromConfirmPage) {
      const storedApplicant = localStorage.getItem("applicant");
      if(storedApplicant) {
        setApplicant(JSON.parse(storedApplicant));
      }

      const storedConsumer = localStorage.getItem("consumer");
      if(storedConsumer) {
        setConsumer(JSON.parse(storedConsumer));
      }

      const storedCancelRequest = localStorage.getItem("cancelRequest");
      if(storedCancelRequest) {
        setCancelRequest(JSON.parse(storedCancelRequest));
      }

      const storedFacilityAgreement = localStorage.getItem("facilityAgreement");
      if(storedFacilityAgreement) {
        setFacilityAgreement(JSON.parse(storedFacilityAgreement));
      }
    } else {
      clearLocalStorage();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: CancelRequestParams = {
      cancelRequest: cancelRequest
    }

    try {
      await confirmCancel(params, custCode);
      setAlert({open: false, messages: []});
      localStorage.setItem("facilityAgreement", JSON.stringify(facilityAgreement));
      localStorage.setItem("applicant", JSON.stringify(applicant));
      localStorage.setItem("consumer", JSON.stringify(consumer));
      localStorage.setItem("cancelRequest", JSON.stringify(cancelRequest));

      navigate(`/facility/facility_agreements/terminate_cancels/${custCode}/cancel/confirm`);
      window.scrollTo(0, 0);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.errors) {
        setAlert({open: true, messages: err.response.data.errors.fullMessages});
        window.scrollTo(0, 0);
      } else {
        setAlert({open: false, messages: []});
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  const displayPlan = (plan: string): string => {
    if(plan === 'both') return "両眼プラン"
    if(plan === 'right') return "片眼プラン(右眼)"
    if(plan === 'left') return "片眼プラン(左眼)"
    return '';
  }

  if (isLoading) {
    return <></>;
  }
  if (isNotFound) {
    return <NotFound />;
  }


  return (
    <Layout>
      <main className="wrapper" id="page-facility_login">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">キャンセル申込入力</h1>
            <AlertMessages open={alert.open} messages={alert.messages} />
            <div className="contact-box">
              <div className="contact-box__left">
                <div className="mbpc-98 mbsp-32">
                  <form onSubmit={handleSubmit}>
                    <div className="contact-box__form js-item" id="id-01">
                      <h2 className="c-ttl__02">お客様情報</h2>
                      <InfoDisplay label="契約種別" value={"定額制契約"} />
                      <InfoDisplay label="お客様ID" value={custCode} />
                      <InfoDisplay label="申込者名(カナ)" value={applicant?.nameKana} />
                      <InfoDisplay label="レンズ使用者名(カナ)" value={consumer?.nameKana} />
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                      <h2 className="c-ttl__02">契約詳細</h2>
                      <InfoDisplay label="ご契約プラン" value={displayPlan(facilityAgreement?.plan ?? '')} />
                      <InfoDisplay label="サービス利用開始日" value={formatDate(facilityAgreement?.startDate ?? '')} />
                      <DateFieldBox
                        label={<>レンズ回収日<br/>(キャンセル申込日)</>}
                        pattern={"d{4}-d{2}-d{2}"}
                        value={cancelRequest.lensCollectionDate}
                        onChange={event => setCancelRequest({...cancelRequest, lensCollectionDate: event.target.value })}
                        required={true}
                      />
                    </div>
                    <button type="submit" className="c-btn__01">入力内容の確認</button>
                  </form>
                </div>
              </div>
              <SidebarNavigation items={[
                { id: "id-01", label: "お客様情報" },
                { id: "id-02", label: "契約詳細" },
                ]} />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default Cancel;